import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { wrapRequest } from 'helpers/requests';

import { StatusPill } from 'ds';
import { getWorkOrderStatusPillProps } from 'ux/Admin/Support/workOrderStatusConfig';

import { media } from 'context';
import { Avatar, Layout, RequestState, Text } from 'ds';
import ClickableDiv from 'ds/ClickableDiv';
import FeedbackThumbControls from 'ds/FeedbackRequests/FeedbackThumbControls';
import { CUSTOMER_SUPPORT_PATH } from 'routes';
import { formatDate } from 'shared/time';
import { selectCustomerSupportTickets } from 'store/AIAssistant/selectors';
import { actions as dashboardActions } from 'store/Dashboard';
import { selectDashboardWidgetConfigs } from 'store/Dashboard/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import { addSupportTicketFeedback } from 'ux/Customer/Support/requests';

import { SupportTicketWidgetData } from './types';

interface Props {
  data: SupportTicketWidgetData;
}

const SupportTicketWidget: React.FC<Props> = ({ data, data: { title, id, reporter, status } }) => {
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const history = useHistory();
  const supportTickets = useAppSelector(selectCustomerSupportTickets);
  const { isMobile } = useContext(media);

  const onVisibleChange = (visible: boolean) => {
    if (!visible) {
      setTimeout(() => {
        setModalIsVisible(visible);
      }, 150);
    } else {
      setModalIsVisible(visible);
    }
  };

  // Find the support ticket by id
  const supportTicket = supportTickets.find(ticket => ticket.id === id);
  const currentStatus = supportTicket?.status ?? status;

  // Get work orders and filter out canceled ones
  const workOrders = supportTicket?.work_orders?.filter(wo => !wo.task?.canceled_at) || [];

  // Calculate work order status with priority logic
  const getWorkOrderStatus = () => {
    if (workOrders.length === 0) return null;

    const needsApproval = workOrders.find(wo => wo.status === 'needs_approval');
    if (needsApproval) return 'needs_approval';

    const scheduling = workOrders.find(wo => wo.status === 'scheduling');
    if (scheduling) return 'scheduling';

    const scheduled = workOrders.find(wo => wo.status === 'scheduled');
    if (scheduled) return 'scheduled';

    // If no priority match, return first work order status
    return workOrders[0]?.status || null;
  };

  // Calculate scheduled date
  const getScheduledDate = () => {
    if (workOrders.length === 0) return null;

    const needsApproval = workOrders.find(wo => wo.status === 'needs_approval');
    if (needsApproval) return null;

    const scheduling = workOrders.find(wo => wo.status === 'scheduling');
    if (scheduling) return null;

    const scheduled = workOrders.find(wo => wo.status === 'scheduled');
    return scheduled?.task?.scheduled_date || null;
  };

  const displayStatus = getWorkOrderStatus();
  const scheduledDate = getScheduledDate();
  const formattedScheduledDate = scheduledDate ? formatDate({ date: scheduledDate, format: 'SHORTEST' }) : null;

  return (
    // <hack> ClickableDiv and storing visiblity state are used here to prevent
    // navigation to the support ticket link. I tried with <Link>, but it was not working.
    <ClickableDiv
      onClick={() => {
        if (modalIsVisible) return;
        history.push(`${CUSTOMER_SUPPORT_PATH}/${id}`);
      }}
      style={{ width: '100%', height: '100%' }}
    >
      <Layout
        direction="column"
        gap={8}
        paddingX={12}
        paddingY={16}
        color="white"
        borderRadius={16}
        height="100%"
        width="100%"
      >
        <Layout align="center" justify="space-between" gap={12}>
          <Text size="body-xs" ellipsis>
            {title}
          </Text>
        </Layout>
        <Layout
          direction="row"
          gap={8}
          align="center"
          justify={isMobile && currentStatus === 'closed' ? 'space-between' : undefined}
        >
          <Layout direction="row" gap={8} align="center">
            <Text size="body-xs" color="gray-600">
              #{id}
            </Text>
            <Avatar size={24} avatarUrl={reporter.avatar_url} />
            {displayStatus && currentStatus !== 'closed' && (
              <StatusPill
                {...getWorkOrderStatusPillProps(displayStatus, formattedScheduledDate)}
                size="sm"
                showDot={false}
              />
            )}
          </Layout>
          {isMobile && currentStatus === 'closed' ? (
            <Layout direction="row" gap={4} align="center">
              <Text size="body-xs" color="gray-400">
                How'd it go?
              </Text>
              <BottomSection
                data={data}
                currentStatus={currentStatus}
                onVisibleChange={onVisibleChange}
                renderFeedbackOnly
              />
            </Layout>
          ) : null}
        </Layout>
        {!(isMobile && currentStatus === 'closed') && (
          <BottomSection data={data} currentStatus={currentStatus} onVisibleChange={onVisibleChange} />
        )}
      </Layout>
    </ClickableDiv>
  );
};

const BottomSection: React.FC<
  Props & {
    currentStatus: SupportTicketWidgetData['status'];
    onVisibleChange: (visible: boolean) => void;
    renderFeedbackOnly?: boolean;
  }
> = ({ data: { id, feedback }, currentStatus, onVisibleChange, renderFeedbackOnly = false }) => {
  const dispatch = useAppDispatch();
  const [requestState, setRequestState] = useState<RequestState>('ready');
  const widgetConfigs = useAppSelector(selectDashboardWidgetConfigs);
  const tenancyId = useAppSelector(selectTenancyId);

  const handleFeedback = async ({ liked, message }: { liked: boolean; message?: string }) => {
    if (!id || !tenancyId || !widgetConfigs) return;

    setRequestState('in_progress');
    await wrapRequest(
      addSupportTicketFeedback({
        supportTicketId: id,
        liked,
        message
      }),
      { successMessage: 'Feedback submitted.' }
    );

    const updatedWidgetConfigs = widgetConfigs.map(row =>
      row.map(config => {
        if (config.type === 'support_ticket' && config.data?.id === id) {
          return {
            ...config,
            data: {
              ...config.data,
              feedback: { liked }
            }
          };
        }
        return config;
      })
    );
    dispatch(dashboardActions.setWidgetConfigs(updatedWidgetConfigs));

    // setTimeout(async () => {
    //   const widgetConfigs = await getDashboardWidgetConfigs({ tenancyId });
    //   dispatch(dashboardActions.setWidgetConfigs(widgetConfigs));
    // }, 1000);
  };

  if (currentStatus === 'closed') {
    if (renderFeedbackOnly) {
      return (
        <FeedbackThumbControls
          liked={feedback?.liked}
          disabled={requestState === 'in_progress'}
          handleFeedback={handleFeedback}
          onVisibleChange={onVisibleChange}
        />
      );
    }
    return (
      <Layout gap={4} align="center">
        <Text size="body-xs" color="gray-400">
          How'd it go?
        </Text>
        <FeedbackThumbControls
          liked={feedback?.liked}
          disabled={requestState === 'in_progress'}
          handleFeedback={handleFeedback}
          onVisibleChange={onVisibleChange}
        />
      </Layout>
    );
  }

  return null;
};

export default SupportTicketWidget;
